import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@/components/ui/accordion';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Download, Globe, Loader2, Pencil, ShieldCheck, AlertTriangle, Copy, Check, FileCode,
  Building2, Stethoscope, ChevronDown, Scale, Printer, FileSignature, ArrowLeftRight,
  Receipt, RefreshCw, FlaskConical, Landmark, User,
} from 'lucide-react';
import { FounderContext } from '@/App';
import PsychosocialSession from './PsychosocialSession';
import PatientCaregiverResources from './PatientCaregiverResources';
import PharmaSurface from './PharmaSurface';
import PayerSurface from './PayerSurface';
import LongitudinalBrief from './LongitudinalBrief';
import { CATALOG_ENGINE_COUNT } from '@/lib/engineCount';
import {
  RiskFlagCard, EvidenceMapBadges, NudgeInbox, OpqrstChip, downloadPDF, TRANSLATE_LANGS,
} from './ClinicalOutput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCLAIMER_TEXT =
  `Draft · ${CATALOG_ENGINE_COUNT} deterministic engines · no LLM in clinical risk path · physician sign-off required.`;

const SURFACES = {
  outpatient: {
    border: '#185FA5', pillBg: '#EFF6FF', pillText: '#185FA5',
    label: 'Outpatient — EMR surface', Icon: Stethoscope,
  },
  hospital: {
    border: '#0F6E56', pillBg: '#F0FDF4', pillText: '#0F6E56',
    label: 'Hospital / rounds — EHR surface', Icon: Building2,
  },
  investor: {
    border: '#7c3aed', pillBg: '#F5F3FF', pillText: '#6d28d9',
    label: 'Investor / compliance — determinism proof', Icon: Scale,
  },
};

// Session 4 (Part B) — five-surface role selector. Outpatient/Hospital render the
// physician surface (different accent); Pharma/Payer render the enterprise surfaces;
// Patient is a NAVIGATION action to the patient "My Health Records" view (not inline).
const ROLE_TABS = [
  { key: 'outpatient', label: 'Outpatient', Icon: Stethoscope, accent: '#185FA5' },
  { key: 'hospital', label: 'Hospital', Icon: Building2, accent: '#0F6E56' },
  { key: 'pharma', label: 'Pharma AI', Icon: FlaskConical, accent: '#B45309' },
  { key: 'payer', label: 'Payer AI', Icon: Landmark, accent: '#B45309' },
  { key: 'investor', label: 'Investor', Icon: Scale, accent: '#7c3aed' },
  { key: 'patient', label: 'Patient', Icon: User, accent: '#185FA5', nav: true },
];

// RED / YELLOW / GREEN per design_guidelines.json risk_flags palette.
const SEV = {
  CRITICAL: { bg: '#FEF2F2', text: '#DC2626', border: '#FECACA' },
  HIGH: { bg: '#FEF2F2', text: '#DC2626', border: '#FECACA' },
  MODERATE: { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
  LOW: { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' },
};
const SEV_RANK = { CRITICAL: 0, HIGH: 1, MODERATE: 2, LOW: 3 };

const stripTag = (s = '') => String(s || '').replace(/^\s*\[[^\]]*\]\s*/, '').trim();
const flagGuideline = (f) => f.source_guideline || f.sourceGuideline || (f.evidence_citations || [])[0] || '';

// ─── Zone 2 exports · deterministic, client-side (no backend, no counter) ───
function buildSoapText(result) {
  const { soap = {}, riskFlags = [], encounterId, visitDate } = result;
  const sd = result.structuredData || {};
  const lines = [];
  lines.push('P.AI Global Health — Physician SOAP Note');
  lines.push(`Encounter: ${encounterId || ''}  |  Date: ${(visitDate || '').slice(0, 10)}`);
  if (sd.patientName) lines.push(`Patient: ${sd.patientName}${sd.age ? `, ${sd.age}` : ''}${sd.sex ? ` ${sd.sex}` : ''}`);
  lines.push('');
  lines.push(`CHIEF COMPLAINT:\n${soap.cc || ''}`);
  lines.push(`\nSUBJECTIVE:\n${soap.subjective || ''}`);
  lines.push(`\nOBJECTIVE:\n${soap.objective || ''}`);
  lines.push(`\nASSESSMENT:\n${soap.assessment || ''}`);
  lines.push(`\nPLAN:\n${soap.plan || ''}`);
  if (riskFlags.length) {
    lines.push('\nRISK FLAGS:');
    riskFlags.forEach((f) => lines.push(`  [${f.severity}] ${f.category}: ${f.message}`));
  }
  lines.push(`\n— Draft · ${CATALOG_ENGINE_COUNT} deterministic engines · no LLM in clinical risk path · physician sign-off required.`);
  return lines.join('\n');
}

function buildFhirBundle(result) {
  const { soap = {}, riskFlags = [], encounterId, visitDate } = result;
  const sd = result.structuredData || {};
  const now = new Date().toISOString();
  const patientId = 'patient-1';
  const esc = (t) => String(t || '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
  const section = (title, text) => ({
    title,
    text: { status: 'generated', div: `<div xmlns="http://www.w3.org/1999/xhtml">${esc(text)}</div>` },
  });
  const entries = [{
    resource: {
      resourceType: 'Patient', id: patientId,
      name: sd.patientName ? [{ text: sd.patientName }] : undefined,
      gender: (sd.sex || '').toLowerCase() || undefined,
    },
  }];
  riskFlags.forEach((f, i) => {
    entries.push({
      resource: {
        resourceType: 'Condition', id: `condition-${i + 1}`,
        clinicalStatus: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-clinical', code: 'active' }] },
        verificationStatus: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status', code: 'provisional' }] },
        code: {
          text: f.category,
        },
        severity: { text: f.severity },
        subject: { reference: `Patient/${patientId}` },
        note: f.message ? [{ text: f.message }] : undefined,
      },
    });
  });
  entries.push({
    resource: {
      resourceType: 'Composition', id: 'composition-1', status: 'preliminary',
      type: { coding: [{ system: 'http://loinc.org', code: '11488-4', display: 'Consult note' }] },
      date: visitDate || now, title: 'SOAP Note',
      subject: { reference: `Patient/${patientId}` },
      section: [
        section('Chief Complaint', soap.cc), section('Subjective', soap.subjective),
        section('Objective', soap.objective), section('Assessment', soap.assessment),
        section('Plan', soap.plan),
      ],
    },
  });
  return {
    resourceType: 'Bundle', type: 'document',
    identifier: { system: 'urn:pai:encounter', value: encounterId || '' },
    timestamp: now, entry: entries,
  };
}

// ─── Shared client-side download / export helpers ───────────────────────────
function downloadBlob(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
}

function exportFhirFile(result, filename) {
  downloadBlob(JSON.stringify(buildFhirBundle(result), null, 2), filename, 'application/fhir+json');
}

// Deterministic, client-side patient consent form (same pattern as the
// off-label attestation HTML). Opens a printable window; falls back to download.
function buildConsentFormHtml(result, legalData) {
  const sd = result.structuredData || {};
  const enc = result.encounterId || '';
  const date = (result.visitDate || new Date().toISOString()).slice(0, 10);
  const offlabel = legalData?.off_label;
  const drugs = (offlabel?.eligible && offlabel.prompts) ? offlabel.prompts : [];
  const drugRows = drugs.map((p) => `<li><strong>${p.drug}</strong>${p.likely_offlabel_use ? ` &mdash; used for ${p.likely_offlabel_use.use} (evidence level ${p.likely_offlabel_use.evidence_level})` : ''}</li>`).join('');
  const offlabelBlock = drugs.length
    ? `<h2>Off-label medication acknowledgement</h2><p>The following medication(s) may be prescribed outside their FDA-approved labeling. Off-label prescribing is legal, common medical practice:</p><ul>${drugRows}</ul>`
    : '<h2>Off-label medication acknowledgement</h2><p>No off-label medication use was detected for this encounter.</p>';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Patient Consent — ${enc}</title>
<style>body{font-family:Georgia,serif;max-width:760px;margin:32px auto;color:#1a2233;line-height:1.6;padding:0 24px}h1{font-size:22px;border-bottom:2px solid #185FA5;padding-bottom:8px}h2{font-size:15px;margin-top:24px;color:#185FA5}.meta{font-size:13px;color:#555}.sig{margin-top:40px}.line{border-bottom:1px solid #333;display:inline-block;min-width:260px;margin:0 6px}.foot{margin-top:32px;font-size:11px;color:#777;border-top:1px solid #eee;padding-top:8px}</style></head><body>
<h1>Informed Consent &amp; Acknowledgement</h1>
<p class="meta">Patient: <strong>${sd.patientName || '________________'}</strong>${sd.age ? ` &middot; Age ${sd.age}` : ''}${sd.sex ? ` &middot; ${sd.sex}` : ''}<br>Encounter: ${enc} &middot; Date: ${date}</p>
<p>I acknowledge that I have discussed my presenting condition, the proposed plan of care, and the associated benefits, risks, and reasonable alternatives with my treating clinician. I have had the opportunity to ask questions and they were answered to my satisfaction.</p>
${offlabelBlock}
<h2>Acknowledgement of decision-support tooling</h2>
<p>I understand that P.AI Global Health is an administrative, rule-based clinical decision-support tool, and that my treating clinician is the sole diagnostic and treatment decision-maker for my care.</p>
<div class="sig">
<p>Patient / representative signature: <span class="line"></span> Date: <span class="line" style="min-width:140px"></span></p>
<p>Clinician signature: <span class="line"></span> Date: <span class="line" style="min-width:140px"></span></p>
</div>
<div class="foot">Deterministic consent form generated client-side from the encounter record. P.AI Global Health &middot; FRE 902(13) legal-protection layer. Draft for clinician review and patient signature; not legal advice.</div>
<script>window.onload=function(){setTimeout(function(){try{window.print()}catch(e){}},400)}</script>
</body></html>`;
}

function openConsentForm(result, legalData) {
  const html = buildConsentFormHtml(result, legalData);
  const w = window.open('', '_blank', 'noopener,noreferrer');
  if (w && w.document) { w.document.open(); w.document.write(html); w.document.close(); }
  else { downloadBlob(html, `${result.encounterId || 'encounter'}_consent.html`, 'text/html'); }
}

const LEVEL_STYLES = {
  HIGH: { bg: '#FEF2F2', text: '#DC2626', border: '#FECACA', label: 'HIGH' },
  MODERATE: { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A', label: 'MODERATE' },
  LOW: { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0', label: 'LOW' },
};

// ─── Zone 2 · Legal Protection Layer (real content from /legal-protection) ──
function LegalProtectionBox({ result, onOpenBilling }) {
  const encounterId = result.encounterId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [attestation, setAttestation] = useState(null);
  const [attesting, setAttesting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`${API}/encounter/${encounterId}/legal-protection`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!active) return;
        const ok = d && !d.error ? d : null;
        setData(ok);
        setAttestation(ok?.attestation || null);
        setLoading(false);
      })
      .catch(() => { if (active) { setData(null); setLoading(false); } });
    return () => { active = false; };
  }, [encounterId]);

  const handleAttest = async () => {
    setAttesting(true);
    try {
      const r = await fetch(`${API}/encounter/${encounterId}/legal-attest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (d.ok && d.attestation) setAttestation(d.attestation);
    } catch { /* noop */ }
    setAttesting(false);
  };

  const mp = data?.malpractice;
  const lv = LEVEL_STYLES[mp?.level] || LEVEL_STYLES.LOW;
  const offlabel = data?.off_label;
  const gaps = data?.documentation_gaps || [];

  return (
    <div id="zone2-legal" data-testid="legal-protection-box" className="border-l-4 border-amber-500 bg-amber-50 p-5 rounded-r-lg my-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Scale className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-amber-900">Legal Protection Layer</h4>
            <div className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 inline-block mt-1">
              FRE 902(13) · hash-sealed
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {loading ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-amber-700"><Loader2 className="w-3.5 h-3.5 animate-spin" /> computing…</span>
          ) : mp ? (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              data-testid="malpractice-badge"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border"
              style={{ background: lv.bg, color: lv.text, borderColor: lv.border }}
            >
              Malpractice exposure: {lv.label} · {mp.score}/100
              <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          ) : (
            <span className="text-xs text-amber-700" data-testid="malpractice-unavailable">Risk score unavailable</span>
          )}
          {attestation ? (
            <span
              data-testid="attested-badge"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border"
              style={{ background: '#F0FDF4', color: '#16A34A', borderColor: '#BBF7D0' }}
              title={`Hash ${attestation.attestation_hash || ''}`}
            >
              <Check className="w-3.5 h-3.5" /> Attested ✓ · {(attestation.attestedAt || '').slice(0, 19).replace('T', ' ')} · {(attestation.attestation_hash || '').slice(0, 10)}…
            </span>
          ) : (
            <Button
              size="sm"
              onClick={handleAttest}
              disabled={attesting || loading}
              data-testid="btn-legal-attest"
              className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {attesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Attest ✓
            </Button>
          )}
        </div>
      </div>

      {expanded && mp && (
        <div className="mt-3 sm:ml-8 bg-white/70 border border-amber-200 rounded p-3 text-xs text-amber-900" data-testid="malpractice-breakdown">
          <div className="font-semibold mb-1">Score breakdown ({mp.score}/100 · {lv.label})</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mb-1">
            <span>CRITICAL × {mp.breakdown.critical}</span>
            <span>HIGH × {mp.breakdown.high}</span>
            <span>MODERATE × {mp.breakdown.moderate}</span>
            <span>Must-exclude × {mp.breakdown.must_exclude_first}</span>
          </div>
          <div className="font-mono text-[10px] text-amber-700">{mp.breakdown.formula}</div>
        </div>
      )}

      <div className="mt-3 sm:ml-8 text-xs">
        {offlabel?.eligible && (offlabel.prompts || []).length > 0 ? (
          <div data-testid="legal-offlabel" className="bg-white border border-amber-300 rounded p-2">
            <div className="font-semibold text-amber-900 mb-1">Off-label use detected ({offlabel.count})</div>
            {offlabel.prompts.map((p, i) => (
              <div key={i} className="text-amber-800">
                <span className="font-semibold">{p.drug}</span>
                {p.likely_offlabel_use && <span> — {p.likely_offlabel_use.use} (evidence {p.likely_offlabel_use.evidence_level})</span>}
                {(p.fda_approved_for || []).length > 0 && <span className="text-amber-600"> · FDA-approved: {p.fda_approved_for.join(', ')}</span>}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-emerald-700" data-testid="legal-offlabel-clear">
            <ShieldCheck className="w-3.5 h-3.5" /> {loading ? 'Checking medications…' : 'No off-label use detected.'}
          </div>
        )}
      </div>

      <div className="mt-2 sm:ml-8 text-xs">
        {gaps.length > 0 ? (
          <div data-testid="legal-doc-gaps">
            <div className="font-semibold text-amber-900 mb-1">Documentation gaps ({gaps.length})</div>
            <ul className="list-disc list-inside space-y-0.5 text-amber-800 max-h-32 overflow-auto">
              {gaps.map((g, i) => <li key={i}>{g}</li>)}
            </ul>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-emerald-700" data-testid="legal-doc-gaps-clear">
            <ShieldCheck className="w-3.5 h-3.5" /> {loading ? 'Checking documentation…' : 'Documentation complete — no gaps detected.'}
          </div>
        )}
      </div>

      {data && (
        <div className="mt-3 sm:ml-8 text-xs" data-testid="legal-avoided-exposure">
          <span className="font-semibold text-amber-900">Est. avoided exposure: ${(data.avoided_exposure_usd || 0).toLocaleString()}</span>
          <div className="text-[10px] text-amber-600">(estimated · deterministic methodology, based on risk-flag severity — not an actuarial or insurance figure)</div>
        </div>
      )}

      {data?.content_hash && (
        <div className="mt-3 sm:ml-8 text-[10px] font-mono text-amber-700 break-all" data-testid="legal-hash">
          SHA-256 {data.content_hash.slice(0, 32)}… · {(data.generated_at || '').slice(0, 19).replace('T', ' ')} UTC
        </div>
      )}

      <div className="mt-4 sm:ml-8 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-amber-400 text-amber-900 hover:bg-amber-100 h-8" onClick={() => window.open(`${API}/moats/f/${encounterId}`, '_blank', 'noopener,noreferrer')} data-testid="btn-print-protection">
          <Printer className="w-3.5 h-3.5 mr-1" /> Print protection doc
        </Button>
        <Button size="sm" variant="outline" className="border-amber-400 text-amber-900 hover:bg-amber-100 h-8" onClick={() => openConsentForm(result, data)} data-testid="btn-consent-form">
          <FileSignature className="w-3.5 h-3.5 mr-1" /> Patient consent form
        </Button>
        <Button size="sm" variant="outline" className="border-amber-400 text-amber-900 hover:bg-amber-100 h-8" onClick={() => exportFhirFile(result, `${encounterId || 'encounter'}_epic_fhir.json`)} data-testid="btn-export-epic">
          <FileCode className="w-3.5 h-3.5 mr-1" /> Download FHIR (Epic-ready)
        </Button>
        <Button size="sm" variant="outline" className="border-amber-400 text-amber-900 hover:bg-amber-100 h-8" onClick={onOpenBilling} data-testid="btn-open-billing">
          <Receipt className="w-3.5 h-3.5 mr-1" /> Billing / E&M codes
        </Button>
      </div>
    </div>
  );
}

// ─── Zone 1 (hospital only) · SBAR inline 2×2 card ──────────────────────────
function SbarInlineCard({ result }) {
  const encounterId = result.encounterId;
  const riskFlags = result.riskFlags || [];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [crossAttest, setCrossAttest] = useState(null);
  const [attesting, setAttesting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`${API}/encounter/${encounterId}/sbar-structured`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!active) return;
        const ok = d && !d.error ? d : null;
        setData(ok);
        setCrossAttest(ok?.cross_shift_attestation || null);
        setLoading(false);
      })
      .catch(() => { if (active) { setData(null); setLoading(false); } });
    return () => { active = false; };
  }, [encounterId]);

  const handleCrossAttest = async () => {
    setAttesting(true);
    try {
      const r = await fetch(`${API}/encounter/${encounterId}/cross-shift-attest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (d.ok && d.attestation) setCrossAttest(d.attestation);
    } catch { /* noop */ }
    setAttesting(false);
  };

  const crossShift = riskFlags.some((f) => /cross.?shift|hand.?off/i.test(f.category || ''));
  const cells = [
    { key: 'situation', label: 'S', name: 'Situation' },
    { key: 'background', label: 'B', name: 'Background' },
    { key: 'assessment', label: 'A', name: 'Assessment' },
    { key: 'recommendation', label: 'R', name: 'Recommendation' },
  ];

  return (
    <div className="rounded-lg border-2 bg-white shadow-sm mb-6 overflow-hidden" style={{ borderColor: '#5DCAA5' }} data-testid="sbar-inline-card">
      <div className="px-4 py-2.5 flex items-center gap-2 border-b" style={{ borderColor: '#5DCAA5', background: '#F0FDF9' }}>
        <ArrowLeftRight className="w-4 h-4" style={{ color: '#0F6E56' }} />
        <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0F6E56' }}>
          SBAR handoff · sbar_generator.py · hash-sealed · FRE 902(13)
        </h3>
      </div>
      {loading ? (
        <div className="p-6 flex items-center justify-center text-sm text-slate-500"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating SBAR…</div>
      ) : data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200" data-testid="sbar-grid">
          {cells.map((c) => (
            <div key={c.key} className="bg-white p-3" data-testid={`sbar-cell-${c.key}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded font-bold text-white text-sm" style={{ background: '#0F6E56' }}>{c.label}</span>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#0F6E56' }}>{c.name}</span>
              </div>
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed max-h-44 overflow-auto">{data[c.key] || '—'}</pre>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 text-sm text-slate-500" data-testid="sbar-unavailable">SBAR unavailable for this encounter.</div>
      )}
      <div className="px-4 py-3 border-t flex flex-wrap items-center gap-2" style={{ borderColor: '#D1FAE5' }}>
        <Button size="sm" variant="outline" className="h-8" onClick={() => exportFhirFile(result, `${encounterId || 'encounter'}_epic_fhir.json`)} data-testid="btn-sbar-export-epic">
          <FileCode className="w-3.5 h-3.5 mr-1" /> Download FHIR (Epic-ready)
        </Button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => window.open(`${API}/encounter/${encounterId}/sbar`, '_blank', 'noopener,noreferrer')} data-testid="btn-print-sbar">
          <Printer className="w-3.5 h-3.5 mr-1" /> Print SBAR
        </Button>
        {crossAttest ? (
          <span
            data-testid="cross-shift-attested-badge"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ml-auto"
            style={{ background: '#F0FDF4', color: '#16A34A', borderColor: '#BBF7D0' }}
            title={`Engine 75 cross-shift attestation · hash ${crossAttest.attestation_hash || ''}`}
          >
            <Check className="w-3.5 h-3.5" />
            Cross-shift attested ✓ · {(crossAttest.attestedAt || '').slice(0, 19).replace('T', ' ')} · {(crossAttest.attestation_hash || '').slice(0, 10)}…
          </span>
        ) : (
          <div className="flex items-center gap-2 ml-auto">
            <span
              data-testid="cross-shift-badge"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
              style={crossShift ? { background: '#FFFBEB', color: '#D97706', borderColor: '#FDE68A' } : { background: '#F1F5F9', color: '#475569', borderColor: '#E2E8F0' }}
              title="Engine 75 — cross-shift attestation gate (read-only status)"
            >
              <RefreshCw className="w-3 h-3" />
              Cross-shift gate: {crossShift ? 'flagged' : 'none'}
            </span>
            <Button
              size="sm"
              onClick={handleCrossAttest}
              disabled={attesting}
              data-testid="btn-cross-shift-attest"
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {attesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Cross-shift attest ✓
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Reusable collapsed accordion section ───────────────────────────────────
function AccSection({ value, title, badge, testid, children }) {
  return (
    <AccordionItem value={value} className="bg-white border border-slate-200 rounded-lg mb-3 overflow-hidden">
      <AccordionTrigger
        data-testid={`accordion-trigger-${testid || value}`}
        className="px-5 py-4 hover:bg-slate-50 hover:no-underline text-base font-semibold text-slate-800"
      >
        <span className="flex items-center gap-2 text-left">
          {title}
          {badge != null && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-semibold">
              {badge}
            </span>
          )}
        </span>
      </AccordionTrigger>
      <AccordionContent className="px-5 pb-5 pt-2 text-slate-600 border-t border-slate-100">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

// ─── Zone 1 · Avoided-cost badge (subtle, gray, collapsible) ────────────────
function AvoidedCostBadge({ preventiveCard }) {
  const [open, setOpen] = useState(false);
  const total = (preventiveCard && preventiveCard.total_avoided_cost_usd) || 0;
  if (!total) return null;
  const matches = (preventiveCard.matches || []).filter((m) => m.avoided_cost_usd > 0);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid="avoided-cost-toggle"
          className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 border border-slate-200 text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-200 transition-colors"
        >
          Est. avoided: ${total.toLocaleString()}
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 w-64 bg-white border border-slate-200 rounded-md p-3 text-[11px] text-slate-600 shadow-sm" data-testid="avoided-cost-detail">
        <div className="font-semibold text-slate-700 mb-1">Est. avoided cost this encounter</div>
        <div className="text-slate-500 mb-2">Projected ER / hospitalization avoidance from matched preventive rules.</div>
        <div className="text-[10px] text-slate-400 mb-2" data-testid="avoided-cost-disclaimer">(estimated, deterministic methodology — not an actuarial or insurance figure)</div>
        <ul className="space-y-1">
          {matches.slice(0, 6).map((m, i) => (
            <li key={m.rule_id || i} className="flex justify-between gap-2">
              <span className="truncate">{m.rule_name}</span>
              <span className="text-emerald-700 font-medium whitespace-nowrap">${(m.avoided_cost_usd || 0).toLocaleString()}</span>
            </li>
          ))}
          {matches.length === 0 && <li className="italic text-slate-400">Aggregate preventive avoidance.</li>}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ─── Zone 1 · History summary one-liner ─────────────────────────────────────
function HistorySummary({ result }) {
  const [open, setOpen] = useState(false);
  const sd = result.structuredData || {};
  const piv = result.patientInputVerbatim || {};
  const subj = result.subjectiveStructured || {};
  const soap = result.soap || {};

  const hpi = stripTag(soap.cc) || (soap.subjective || '').split('\n').filter(Boolean)[0] || '—';
  const pmh = sd.existingConditions || piv.existingConditions || '';
  const psh = result.surgicalHistoryFreeText
    || (result.surgicalHistoryRows || []).filter((r) => r.procedure).map((r) => `${r.procedure}${r.year ? ` (${r.year})` : ''}`).join(', ')
    || '';
  const fh = subj.familyHistory || '';
  const meds = sd.medications || piv.medications || '';
  const allergies = subj.allergies || '';

  const seg = [];
  seg.push(['HPI', hpi]);
  if (pmh) seg.push(['PMH', pmh]);
  if (psh) seg.push(['PSH', psh]);
  if (fh) seg.push(['Family hx', fh]);
  if (meds) seg.push(['Meds', meds]);
  if (allergies) seg.push(['Allergies', allergies]);

  return (
    <div
      data-testid="zone1-history-summary"
      className="bg-slate-50 p-4 rounded-md border border-slate-200 text-sm leading-relaxed text-slate-700 mb-6"
    >
      <div className={open ? '' : 'lc-3'}>
        {seg.map(([label, val], i) => (
          <span key={label}>
            {i > 0 && <span className="text-slate-300"> · </span>}
            <span className="font-medium text-slate-500">{label}: </span>
            <span>{val}</span>
          </span>
        ))}
      </div>
      {seg.length > 2 && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="history-summary-toggle"
          className="mt-1.5 text-xs font-medium text-slate-500 hover:text-slate-700"
        >
          {open ? 'Show less' : 'Show full history'}
        </button>
      )}
    </div>
  );
}

// ─── Zone 1 · Worst-first differential cards (top 3 risk flags) ─────────────
function DifferentialCard({ flag, rank }) {
  const sev = (flag.severity || 'MODERATE').toUpperCase();
  const s = SEV[sev] || SEV.MODERATE;
  const guideline = flagGuideline(flag);
  const engine = flag.source_engine || flag.engine || flag.engine_id;
  return (
    <div
      data-testid={`differential-card-${rank - 1}`}
      className="p-4 border border-slate-200 rounded-lg bg-white shadow-sm flex flex-col gap-2 relative overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 ease-in-out"
    >
      <span className="absolute -right-2 -bottom-4 text-6xl font-black text-slate-100 opacity-70 pointer-events-none select-none">
        {rank}
      </span>
      <div className="flex items-center justify-between gap-2 relative z-10">
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full border"
          style={{ background: s.bg, color: s.text, borderColor: s.border }}
        >
          {sev}
        </span>
      </div>
      <h3 className="text-sm font-semibold tracking-tight text-slate-900 relative z-10">{flag.category}</h3>
      {guideline && <div className="text-xs text-slate-500 relative z-10">{guideline}</div>}
      {engine && (
        <span className="self-start text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200 relative z-10">
          {engine}
        </span>
      )}
    </div>
  );
}

// ─── Zone 1 · Encounter-specific safety alerts ──────────────────────────────
function SafetyAlerts({ result }) {
  const { soap, transcriptionWarnings = [], spellingAmbiguities = [] } = result;
  const negation = soap?.negationAudit?.negation_errors_found ? soap.negationAudit : null;
  const lowConf = soap?.structuredSoap?.validation?.confidence_floor_met === false;
  if (!negation && !lowConf && transcriptionWarnings.length === 0 && spellingAmbiguities.length === 0) return null;
  return (
    <div className="space-y-2 mb-6" data-testid="physician-safety-alerts">
      {negation && (
        <div
          className={`rounded-md p-3 text-sm flex items-start gap-2 ${negation.blocked ? 'bg-red-50 border-2 border-red-300 text-red-800' : 'bg-amber-50 border border-amber-300 text-amber-800'}`}
          data-testid="negation-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-bold">{negation.blocked ? 'Negation error — review required' : 'Negation warning'}</p>
            {negation.errors?.map((e, i) => (
              <p key={i} className="text-xs mt-0.5">[{e.severity}] {e.patient_said} → but {e.soap_assertion}</p>
            ))}
          </div>
        </div>
      )}
      {lowConf && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-md p-3 text-sm text-yellow-800 flex items-start gap-2" data-testid="low-confidence-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Low confidence ({soap.structuredSoap.assessment.confidence_score}) — physician review required. Ask patient for additional information.</span>
        </div>
      )}
      {transcriptionWarnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-md p-3 text-xs sm:text-sm text-yellow-900" data-testid="transcription-warning-block">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-700" />
            <div>
              <p className="font-semibold mb-1">Possible transcription error — please verify patient input.</p>
              <ul className="list-disc list-inside space-y-0.5">
                {transcriptionWarnings.map((w, i) => (
                  <li key={i} data-testid={`transcription-warning-${i}`}>{w.detail || w.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {spellingAmbiguities.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-md p-3 text-xs sm:text-sm text-yellow-900" data-testid="spelling-ambiguity-block">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-700" />
            <div>
              <p className="font-semibold mb-1">Term(s) unresolved — please verify before relying on this SOAP:</p>
              <ul className="list-disc list-inside space-y-0.5">
                {spellingAmbiguities.map((amb, i) => (
                  <li key={i}>
                    <code className="px-1 py-0.5 bg-yellow-100 rounded">{amb.original || amb.found || amb.term || JSON.stringify(amb)}</code>
                    {amb.options && Array.isArray(amb.options) && <span className="text-slate-600"> — possible meanings: {amb.options.join(', ')}</span>}
                    {amb.intended && <span className="text-slate-600"> — did the patient mean &ldquo;{amb.intended}&rdquo;?</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Physician View dropdown alternate bodies (ported verbatim from ClinicalOutput) ──
function PhysicianSummaryView({ soap, riskFlags }) {
  return (
    <div className="space-y-3 mb-6" data-testid="view-physician-summary">
      <div className="bg-slate-50 border-2 border-[#0369A1]/30 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-wide font-bold text-[#0369A1] mb-2">Condensed Physician Summary</div>
        <p className="text-sm text-slate-800 mb-2"><strong>Chief complaint:</strong> {soap?.cc || '—'}</p>
        {soap?.assessment && (
          <p className="text-sm text-slate-800 mb-2"><strong>Assessment (1-liner):</strong> {(soap.assessment.split('\n').filter(Boolean)[0] || '').slice(0, 240)}</p>
        )}
        {riskFlags?.length > 0 && (
          <div className="mb-2">
            <p className="text-sm font-bold text-slate-800 mb-1">Top {Math.min(3, riskFlags.length)} flag(s):</p>
            <ul className="text-sm text-slate-700 list-disc list-inside space-y-0.5">
              {riskFlags.slice(0, 3).map((flag, i) => (
                <li key={`sum-flag-${i}`} data-testid={`summary-flag-${i}`}>
                  <strong>[{flag.severity}]</strong> {flag.category} — {flag.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-xs italic text-slate-500 mt-2">Suggested — physician review required. Switch to <em>Full SOAP</em> for complete S/O/A/P body.</p>
      </div>
    </div>
  );
}

function PastHistoryView({ result, soap }) {
  return (
    <div className="space-y-3 mb-6" data-testid="view-past-history">
      <div className="bg-slate-50 border-2 border-[#0369A1]/30 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-wide font-bold text-[#0369A1] mb-2">Past History &amp; Clinical Notes</div>
        <div className="mb-3 pb-3 border-b border-slate-200">
          <p className="text-xs font-bold text-slate-600 uppercase mb-1">This visit</p>
          <p className="text-sm text-slate-800"><strong>Chief complaint:</strong> {soap?.cc || '—'}</p>
          {soap?.subjective && <p className="text-sm text-slate-700 mt-1"><strong>Subjective:</strong> {soap.subjective.slice(0, 320)}</p>}
        </div>
        {(result?.patientInputVerbatim?.existingConditions || result?.existingConditions) && (
          <div className="mb-3">
            <p className="text-xs font-bold text-slate-600 uppercase mb-1">Existing conditions (patient-reported)</p>
            <p className="text-sm text-slate-700">{result?.patientInputVerbatim?.existingConditions || result?.existingConditions || '—'}</p>
          </div>
        )}
        {(result?.patientInputVerbatim?.medications || result?.medications) && (
          <div className="mb-3">
            <p className="text-xs font-bold text-slate-600 uppercase mb-1">Current medications</p>
            <p className="text-sm text-slate-700">{result?.patientInputVerbatim?.medications || result?.medications || '—'}</p>
          </div>
        )}
        {(result?.surgicalHistoryRows?.length > 0 || result?.surgicalHistoryFreeText) && (
          <div className="mb-3">
            <p className="text-xs font-bold text-slate-600 uppercase mb-1">Past surgical history (chronological)</p>
            {result?.surgicalHistoryRows?.length > 0 && (
              <ul className="text-sm text-slate-700 list-disc list-inside space-y-0.5">
                {[...result.surgicalHistoryRows]
                  .sort((a, b) => (parseInt(b.year) || 0) - (parseInt(a.year) || 0))
                  .map((row, i) => row.procedure && (
                    <li key={`sx-hist-${i}`} data-testid={`history-sx-${i}`}>
                      <strong>{row.year || '?'}</strong> — {row.procedure}
                      {row.facility && <span className="text-slate-500"> · {row.facility}</span>}
                      {row.complication && <span className="text-red-600 font-semibold"> · Complication: {row.complication}</span>}
                    </li>
                  ))}
              </ul>
            )}
            {result?.surgicalHistoryFreeText && (
              <p className="text-sm text-slate-700 mt-1 italic">&ldquo;{result.surgicalHistoryFreeText}&rdquo;</p>
            )}
          </div>
        )}
        <p className="text-xs italic text-slate-500 mt-2 border-l-2 border-amber-400 pl-2">
          Multi-visit longitudinal timeline (prior encounter aggregation) will populate from longitudinal-memory step in production. Switch to <em>Full SOAP</em> for current-visit S/O/A/P body.
        </p>
      </div>
    </div>
  );
}

function ProvenanceView({ riskFlags }) {
  return (
    <div className="space-y-3 mb-6" data-testid="view-provenance">
      <div className="bg-slate-50 border-2 border-[#0369A1]/30 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-wide font-bold text-[#0369A1] mb-2">Differentials &amp; Engine Provenance</div>
        <p className="text-xs text-slate-600 mb-3">Each flag traced to its rule_id, source guideline, and the patient-text trigger that fired it. Cures Act-aligned audit trail.</p>
        {riskFlags?.length > 0 ? (
          (() => {
            const groups = {};
            riskFlags.forEach((f) => {
              const key = (f.source_engine || f.engine || f.rule_id || f.ruleId || 'unclassified_engine');
              if (!groups[key]) groups[key] = [];
              groups[key].push(f);
            });
            return Object.entries(groups).map(([engineKey, flags], gi) => (
              <div key={`prov-eng-${gi}`} className="mb-3 pb-3 border-b border-slate-200 last:border-b-0" data-testid={`provenance-engine-${engineKey}`}>
                <p className="text-[11px] font-mono font-bold text-slate-600 mb-1">Engine: {engineKey}</p>
                {flags.map((flag, fi) => (
                  <div key={`prov-flag-${gi}-${fi}`} className="ml-2 mb-2">
                    <p className="text-sm text-slate-800"><strong>[{flag.severity}]</strong> {flag.category} — {flag.message}</p>
                    <div className="text-[11px] text-slate-500 ml-2 mt-0.5 leading-snug">
                      <div><span className="font-mono">source_engine:</span> {flag.source_engine || flag.engine || '(unclassified)'}</div>
                      <div><span className="font-mono">rule_id:</span> {flag.rule_id || flag.ruleId || flag.category || '(n/a)'}</div>
                      <div><span className="font-mono">source_guideline:</span> {flag.source_guideline || flag.sourceGuideline || 'guideline_pending'}</div>
                      {flag.triggers?.length > 0 && (
                        <div><span className="font-mono">trigger_tokens:</span> {flag.triggers.join(' | ')}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ));
          })()
        ) : (
          <p className="text-sm text-slate-500 italic">No flags fired on this encounter. Nothing to audit.</p>
        )}
      </div>
    </div>
  );
}

// History + Subjective (S) only — added July 2026 per physician request
// (Dr. Jay / Dr. Devarajan). Subjective narrative + past history, no O/A/P.
function HistorySubjectiveView({ result, soap }) {
  return (
    <div className="space-y-3 mb-6" data-testid="view-history-subjective">
      <div className="bg-slate-50 border-2 border-[#0369A1]/30 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-wide font-bold text-[#0369A1] mb-2">History + Subjective (S) only</div>
        <p className="text-sm text-slate-800 mb-2"><strong>Chief complaint:</strong> {soap?.cc || '—'}</p>
        <div className="mb-3 pb-3 border-b border-slate-200">
          <p className="text-xs font-bold text-slate-600 uppercase mb-1">Subjective (patient narrative)</p>
          <p className="text-sm text-slate-700 whitespace-pre-line">{soap?.subjective || '—'}</p>
        </div>
        {(result?.patientInputVerbatim?.existingConditions || result?.existingConditions) && (
          <div className="mb-2">
            <p className="text-xs font-bold text-slate-600 uppercase mb-1">Existing conditions (patient-reported)</p>
            <p className="text-sm text-slate-700">{result?.patientInputVerbatim?.existingConditions || result?.existingConditions}</p>
          </div>
        )}
        {(result?.patientInputVerbatim?.medications || result?.medications) && (
          <div className="mb-2">
            <p className="text-xs font-bold text-slate-600 uppercase mb-1">Current medications</p>
            <p className="text-sm text-slate-700">{result?.patientInputVerbatim?.medications || result?.medications}</p>
          </div>
        )}
        <p className="text-xs italic text-slate-500 mt-2">Subjective + history only. Switch to <em>Full SOAP</em> for the Objective / Assessment / Plan body.</p>
      </div>
    </div>
  );
}

// Investor / compliance surface — determinism proof (moved off the physician
// view July 2026): engine provenance + structured JSON live here, not in the
// clinical SOAP.
function InvestorSurface({ result, riskFlags, structuredData }) {
  return (
    <div className="mt-4 space-y-4" data-testid="investor-surface">
      <div className="rounded-xl border-2 border-violet-300 bg-violet-50/60 p-4">
        <h3 className="text-sm font-bold text-violet-800 mb-1">Investor / Compliance — Determinism Proof</h3>
        <p className="text-xs text-slate-600">Not a physician-facing clinical view. Engine provenance and the raw structured output are shown here as evidence of deterministic, auditable behaviour (rule_id → source guideline → trigger tokens; ONC Cures Act alignment).</p>
      </div>
      <ProvenanceView riskFlags={riskFlags} />
      <div className="bg-slate-50 border-2 border-violet-200 rounded-xl p-4" data-testid="investor-structured-json">
        <div className="text-[10px] uppercase tracking-wide font-bold text-violet-700 mb-2">Structured data (JSON)</div>
        <pre className="text-xs text-slate-700 bg-white p-4 rounded-md overflow-auto font-mono max-h-96">{JSON.stringify(structuredData || {}, null, 2)}</pre>
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function PhysicianOutput({ result, onEdit, onViewPatient }) {
  const { isFounder } = useContext(FounderContext);
  const [surface, setSurface] = useState('outpatient');
  const [physicianView, setPhysicianView] = useState('history');
  const PHYSICIAN_VIEWS = [
    { value: 'history', label: 'Past History + Clinical Notes' },
    { value: 'subjective', label: 'History + Subjective (S)' },
    { value: 'full', label: 'Full SOAP' },
    { value: 'summary', label: 'Physician Summary' },
  ];
  const [translateLang, setTranslateLang] = useState('hi-IN');
  const [translatedSoap, setTranslatedSoap] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(null);
  const [showAttestation, setShowAttestation] = useState(false);
  const [attestChecked1, setAttestChecked1] = useState(false);
  const [attestChecked2, setAttestChecked2] = useState(false);
  const [copied, setCopied] = useState(false);
  const [billingValue, setBillingValue] = useState([]);

  if (!result) return null;

  const {
    encounterId, visitDate, soap = {}, riskFlags = [], guardrails, auditLog = [],
    healthMartRecommendations = [], structuredData = {}, clinicalSummary,
    motivationSupport, providerFollowup = [], lonelinessSupport, psychosocialOffer,
    drugAudit = { drugs: [], classes: [] }, preventiveRecommendations = [],
    preventiveMedicineCard = {}, differentials = [], resourceBundles,
  } = result;

  const sd = structuredData || {};
  const patientName = sd.patientName || result.patientInputVerbatim?.patientName || 'Patient';
  const surf = SURFACES[surface] || SURFACES.outpatient;

  const sortedFlags = [...riskFlags].sort(
    (a, b) => (SEV_RANK[(a.severity || 'MODERATE').toUpperCase()] ?? 2) - (SEV_RANK[(b.severity || 'MODERATE').toUpperCase()] ?? 2)
  );
  const topThree = sortedFlags.slice(0, 3);

  const hasBilling = (soap.cptSupportCues || []).length > 0;

  const openBilling = () => {
    if (!hasBilling) return;
    setBillingValue(['billing']);
    setTimeout(() => document.getElementById('zone2-billing')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  };

  const hasPreventive = preventiveRecommendations.length > 0 || (preventiveMedicineCard.matches || []).length > 0;
  const hasMeds = (drugAudit.drugs || []).length > 0;
  const hasTakeHome = (soap.patientBridge || []).length > 0 || motivationSupport || providerFollowup.length > 0;
  const hasPsychosocial = psychosocialOffer?.eligible || lonelinessSupport?.card || (resourceBundles && (resourceBundles.bundles || resourceBundles).length);

  const handleTranslate = async () => {
    if (!soap) return;
    setIsTranslating(true); setTranslateError(null); setTranslatedSoap(null);
    const soapText = [
      `Chief Complaint: ${soap.cc || ''}`,
      `\nSubjective:\n${soap.subjective || ''}`,
      `\nAssessment:\n${soap.assessment || ''}`,
      `\nPlan:\n${soap.plan || ''}`,
      riskFlags?.length ? `\nRisk Flags: ${riskFlags.map((f) => `[${f.severity}] ${f.category}: ${f.message}`).join('; ')}` : '',
    ].filter(Boolean).join('\n');
    try {
      const res = await fetch(`${API}/sarvam/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: soapText, source_language_code: 'en-IN', target_language_code: translateLang, mode: 'formal' }),
      });
      const data = await res.json();
      if (data.translated_text) setTranslatedSoap(data.translated_text);
      else setTranslateError(data.error || 'Translation failed');
    } catch (err) {
      setTranslateError('Network error. Please try again.');
    }
    setIsTranslating(false);
  };

  const copySoap = async () => {
    const text = buildSoapText(result);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older / non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const exportFhir = () => exportFhirFile(result, `${encounterId || 'encounter'}_fhir_r4.json`);

  return (
    <section className="physician-output max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="clinical-output">
      {/* ─── Session 4 (Part B) · Five-surface role selector ─────────── */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 pb-px" data-testid="role-tab-bar">
        {ROLE_TABS.map((t) => {
          const active = !t.nav && surface === t.key;
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`role-tab-${t.key}`}
              onClick={() => (t.nav ? onViewPatient && onViewPatient() : setSurface(t.key))}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-t-lg border-b-2 -mb-px transition-colors ${
                active ? 'bg-white text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
              }`}
              style={active ? { borderBottomColor: t.accent, color: t.accent } : undefined}
            >
              <t.Icon className="w-4 h-4" />
              {t.label}
              {t.nav && <span className="text-[9px] uppercase tracking-wide text-slate-400 ml-0.5">↗ records</span>}
            </button>
          );
        })}
      </div>

      {surface === 'pharma' && <PharmaSurface />}
      {surface === 'payer' && <PayerSurface />}
      {surface === 'investor' && (
        <InvestorSurface result={result} riskFlags={riskFlags} structuredData={structuredData} />
      )}

      {(surface === 'outpatient' || surface === 'hospital') && (
      <div
        className="relative bg-white rounded-lg border border-slate-200 shadow-sm p-6 md:p-8 mt-4"
        style={{ borderTopWidth: '2px', borderTopColor: surf.border }}
        data-testid="physician-output-card"
      >
        {/* Surface pill straddling the top border */}
        <span
          data-testid="surface-pill"
          className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 text-xs font-bold rounded-b-md shadow-sm inline-flex items-center gap-1.5"
          style={{ background: surf.pillBg, color: surf.pillText, borderTop: `2px solid ${surf.border}` }}
        >
          <surf.Icon className="w-3 h-3" />
          {surf.label}
        </span>

        {/* ─── ZONE 1 — Always visible ─────────────────────────────── */}
        {/* Row 1 · Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-testid="result-patient-name">{patientName}</h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-sm text-slate-500">
              {sd.age && <span>{sd.age}{sd.sex ? ` · ${sd.sex}` : ''}</span>}
              {sd.age && <span className="text-slate-300">·</span>}
              <span className="font-mono" data-testid="result-encounter-id">{encounterId}</span>
              <span className="text-slate-300">·</span>
              <span>{(visitDate || '').slice(0, 10)}</span>
            </div>
            <span
              data-testid="disclaimer-pill"
              className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200"
            >
              {DISCLAIMER_TEXT}
            </span>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <AvoidedCostBadge preventiveCard={preventiveMedicineCard} />
            {isFounder && onEdit && (
              <Button size="sm" onClick={() => onEdit(encounterId)} className="bg-amber-500 hover:bg-amber-600 text-white h-8" data-testid="btn-edit-intake">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Intake
              </Button>
            )}
          </div>
        </div>

        {/* Encounter-specific safety alerts */}
        <SafetyAlerts result={result} />

        {/* Row 2 · History summary */}
        <HistorySummary result={result} />

        {/* Physician View selector — PROMINENT (red box, white label) per
            founder request July 2026. Default = Past History + Clinical Notes.
            Provenance moved to the Investor surface tab. */}
        <div className="mb-4 rounded-xl border-2 border-red-600 bg-red-600 p-3 shadow-sm" data-testid="physician-view-selector">
          <span className="text-sm font-extrabold uppercase tracking-wide text-white flex items-center gap-1.5 mb-2">
            <Stethoscope className="w-4 h-4" /> Physician View
          </span>
          <div className="flex flex-wrap gap-2" role="tablist" data-testid="physician-view-tabs">
            {PHYSICIAN_VIEWS.map((v) => {
              const active = physicianView === v.value;
              return (
                <button
                  key={v.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setPhysicianView(v.value)}
                  data-testid={`view-tab-${v.value}`}
                  className={`px-4 py-2 rounded-lg text-base font-bold border-2 transition-colors ${active ? 'bg-white text-red-700 border-white shadow-sm' : 'bg-red-500 text-white border-white/60 hover:bg-red-400'}`}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>

        {physicianView === 'summary' && <PhysicianSummaryView soap={soap} riskFlags={riskFlags} />}
        {physicianView === 'history' && <PastHistoryView result={result} soap={soap} />}
        {physicianView === 'subjective' && <HistorySubjectiveView result={result} soap={soap} />}

        {physicianView === 'full' && (
        <>
        {/* Row 3 · Risk flag badges */}
        <div className="mb-6">
          <div className="text-xs font-medium tracking-wide text-slate-500 uppercase mb-2">Risk flags</div>
          {riskFlags.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="risk-flag-badges">
              {riskFlags.map((f, i) => {
                const sev = (f.severity || 'MODERATE').toUpperCase();
                const s = SEV[sev] || SEV.MODERATE;
                return (
                  <span
                    key={i}
                    data-testid={`risk-flag-badge-${i}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    style={{ background: s.bg, color: s.text, borderColor: s.border }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.text }} />
                    {f.category}
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-emerald-700 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" /> No red-flag patterns fired this encounter.
            </div>
          )}
        </div>

        {/* SBAR inline card — hospital / rounds view only (Zone 1) */}
        {surface === 'hospital' && <SbarInlineCard result={result} />}

        {/* Row 4 · Worst-first differential */}
        {topThree.length > 0 && (
          <div className="mb-2">
            <div className="text-xs font-medium tracking-wide text-slate-500 uppercase mb-2">
              Worst-first differential · {CATALOG_ENGINE_COUNT} engines · no LLM
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="worst-first-differential">
              {topThree.map((f, i) => <DifferentialCard key={i} flag={f} rank={i + 1} />)}
            </div>
          </div>
        )}

        <hr className="my-8 border-slate-100" />

        {/* ─── ZONE 2 — Core documentation (always expanded, frictionless) ─── */}
        <div className="w-full bg-white border border-slate-200 rounded-lg mb-3 p-5" data-testid="soap">
          <h3 className="text-xl font-extrabold text-slate-900 mb-4">Full SOAP note</h3>
            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Button size="sm" variant="outline" onClick={() => setShowAttestation(true)} data-testid="btn-soap-pdf">
                <Download className="w-3.5 h-3.5 mr-1" /> Export SOAP (PDF)
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadPDF(`${API}/encounter/${encounterId}/patient-pdf`)} data-testid="btn-patient-pdf">
                <Download className="w-3.5 h-3.5 mr-1" /> Patient PDF
              </Button>
              <Button size="sm" variant="outline" onClick={copySoap} data-testid="btn-copy-soap">
                {copied ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                {copied ? 'Copied!' : 'Copy SOAP'}
              </Button>
              <Button size="sm" variant="outline" onClick={exportFhir} data-testid="btn-export-fhir" title="Download a FHIR R4 document Bundle (Composition + Conditions) for EMR import">
                <FileCode className="w-3.5 h-3.5 mr-1" /> Export FHIR (R4)
              </Button>
              <div className="flex items-center gap-1.5 ml-auto" data-testid="translate-section">
                <Select value={translateLang} onValueChange={setTranslateLang}>
                  <SelectTrigger className="h-8 text-xs w-[130px] border-slate-300" data-testid="translate-lang-select">
                    <Globe className="w-3 h-3 mr-1 text-[#185FA5]" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {Object.entries(TRANSLATE_LANGS).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={handleTranslate} disabled={isTranslating} className="h-8 text-xs text-white" style={{ background: surf.border }} data-testid="translate-btn">
                  {isTranslating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Globe className="w-3 h-3 mr-1" />}
                  Translate
                </Button>
              </div>
            </div>

            {translateError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-sm text-red-700" data-testid="translate-error">
                Translation error: {translateError}
              </div>
            )}
            {translatedSoap && (
              <div className="bg-sky-50 border-2 rounded-md p-4 mb-4" style={{ borderColor: surf.border }} data-testid="translated-soap">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-sm flex items-center gap-1.5" style={{ color: surf.border }}>
                    <Globe className="w-4 h-4" /> SOAP Note — {TRANSLATE_LANGS[translateLang] || translateLang}
                  </h4>
                  <button onClick={() => setTranslatedSoap(null)} className="text-xs text-slate-400 hover:text-slate-600">Close</button>
                </div>
                <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">{translatedSoap}</pre>
                <p className="text-[10px] text-slate-400 mt-2">Rule-based dictionary translation for patient communication (Sarvam AI). Not a replacement for the English clinical record.</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <div className="soap-section-title">CC (Chief Complaint)</div>
                <p className="text-base font-medium text-slate-800">{soap.cc}</p>
              </div>
              <div>
                <div className="soap-section-title">Subjective</div>
                <pre className="text-base font-medium text-slate-800 whitespace-pre-wrap font-sans">{soap.subjective}</pre>
              </div>
              <div>
                <div className="soap-section-title">Objective</div>
                <p className="text-base text-slate-600 italic">{soap.objective}</p>
              </div>
              <div>
                <div className="soap-section-title">Assessment</div>
                <pre className="text-base font-medium text-slate-800 whitespace-pre-wrap font-sans">{soap.assessment}</pre>
              </div>
              <OpqrstChip cc={result.chiefComplaintStructured} />
              <div>
                <div className="soap-section-title">Plan</div>
                <pre className="text-base font-medium text-slate-800 whitespace-pre-wrap font-sans">{soap.plan}</pre>
              </div>
              {soap.physicianSummary && (
                <div className="border-l-4 pl-3 py-1" style={{ borderColor: surf.border }}>
                  <div className="soap-section-title" style={{ borderColor: 'transparent' }}>Physician summary</div>
                  <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">{soap.physicianSummary}</pre>
                </div>
              )}
            </div>
        </div>

        {/* Session 5 — Longitudinal patient brief (all visits), between Full SOAP and Legal Protection */}
        <Accordion type="multiple" className="w-full mt-4">
          <AccSection value="longitudinal" title="Longitudinal patient brief — all visits" testid="longitudinal-brief-accordion">
            <LongitudinalBrief result={result} />
          </AccSection>
        </Accordion>

        {/* Legal Protection Layer — real content from /legal-protection (always rendered, both surfaces) */}
        <LegalProtectionBox result={result} onOpenBilling={openBilling} />

        {hasBilling && (
          <div id="zone2-billing">
            <Accordion type="multiple" className="w-full" value={billingValue} onValueChange={setBillingValue}>
              <AccSection value="billing" title="E/M · billing support codes" testid="billing-support" badge={(soap.cptSupportCues || []).length || undefined}>
              {(soap.cptSupportCues || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-xs font-semibold text-slate-600 mb-1">E/M support cues</div>
                  <ul className="list-disc list-inside text-xs text-slate-600 space-y-0.5">
                    {soap.cptSupportCues.map((c, i) => <li key={i}>{typeof c === 'string' ? c : (c.cue || c.text || JSON.stringify(c))}</li>)}
                  </ul>
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-2">DRAFT — possible suggestions for clinician review. Not final billing or diagnostic codes.</p>
            </AccSection>
          </Accordion>
          </div>
        )}

        {/* ─── ZONE 3 — Ancillary (all collapsed) ──────────────────── */}
        <Accordion type="multiple" className="w-full">
          {hasPreventive && (
            <AccSection value="preventive" title="Preventive recommendations (USPSTF Grade A/B)" testid="preventive" badge={preventiveRecommendations.length || (preventiveMedicineCard.matches || []).length || undefined}>
              {preventiveRecommendations.length > 0 && (
                <div className="space-y-2 mb-3">
                  {preventiveRecommendations.map((rec, i) => (
                    <div key={rec.rec_id || i} className="bg-white border border-emerald-200 rounded-md p-3 text-sm" data-testid={`uspstf-rec-${rec.rec_id || i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold text-white ${rec.grade === 'A' ? 'bg-emerald-600' : 'bg-emerald-400'}`}>Grade {rec.grade}</span>
                        <span className="font-semibold text-slate-900">{rec.topic}</span>
                        {rec.recency_status && (
                          <span
                            data-testid={`uspstf-recency-${rec.rec_id || i}`}
                            className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${rec.recency_status === 'overdue' ? 'bg-red-600 text-white' : rec.recency_status === 'up_to_date' ? 'bg-slate-200 text-slate-700' : 'bg-amber-400 text-amber-950'}`}
                          >
                            {rec.recency_status === 'overdue' ? 'OVERDUE' : rec.recency_status === 'up_to_date' ? 'UP TO DATE' : 'NO PRIOR ON RECORD'}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400 ml-auto">{rec.rec_id}</span>
                      </div>
                      <div className="text-slate-700">{rec.plan_text}</div>
                      {rec.recency_note && (
                        <div className={`text-[11px] mt-1 font-medium ${rec.recency_status === 'overdue' ? 'text-red-700' : 'text-slate-500'}`} data-testid={`uspstf-recency-note-${rec.rec_id || i}`}>{rec.recency_note}</div>
                      )}
                      <div className="text-[11px] text-slate-500 italic mt-1">Eligibility: {rec.population}</div>
                    </div>
                  ))}
                </div>
              )}
              {(preventiveMedicineCard.matches || []).length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-slate-600">Preventive medicine — WHO 4-level framework</div>
                  {preventiveMedicineCard.matches.map((m, i) => (
                    <div key={m.rule_id || i} className="bg-white border border-indigo-200 rounded-md p-3 text-sm" data-testid={`prevencard-rule-${m.rule_id}`}>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] px-2 py-0.5 rounded font-bold bg-indigo-600 text-white">{m.prevention_level}</span>
                        <span className="font-semibold text-slate-900">{m.rule_name}</span>
                        {m.evidence_grade && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-200">Grade {m.evidence_grade}</span>}
                      </div>
                      <div className="text-slate-700">{m.recommendation_text}</div>
                      <div className="text-[11px] text-slate-500 italic mt-1">Source: {m.source_code}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3"><NudgeInbox encounterId={encounterId} /></div>
            </AccSection>
          )}

          {hasMeds && (
            <AccSection value="meds" title="Medication review (normalizer · interactions · Beers)" testid="medications" badge={(drugAudit.drugs || []).length || undefined}>
              <div className="space-y-1 text-sm">
                {(drugAudit.drugs || []).map((d, i) => (
                  <div key={i} className="flex items-baseline gap-2">
                    <code className="px-1.5 py-0.5 bg-slate-100 rounded font-semibold text-slate-800">{d.mention}</code>
                    <span className="text-slate-400">→</span>
                    <span className="text-slate-700">{d.generic}</span>
                    <span className="text-[10px] text-slate-500 ml-auto">{d.is_brand ? 'brand' : 'generic'} · {d['class']}</span>
                  </div>
                ))}
              </div>
              {(drugAudit.classes || []).length > 0 && (
                <div className="text-[11px] text-slate-600 pt-2 border-t border-slate-100 mt-2">
                  <span className="font-semibold">Distinct drug classes:</span> {(drugAudit.classes || []).join(' · ')}
                </div>
              )}
            </AccSection>
          )}

          {differentials.length > 0 && (
            <AccSection value="ddx" title="Differential diagnosis (rule-out tests)" testid="differential-ddx" badge={differentials.length}>
              <div className="space-y-2">
                {differentials.map((dx, i) => (
                  <div key={i} className="border border-slate-200 rounded-md p-3" data-testid={`differential-${i}`}>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-slate-800 text-white">{dx.urgency}</span>
                      <span className="font-semibold text-slate-900 text-sm">{dx.condition}</span>
                      {dx.parentFlagCategory && <span className="text-[10px] text-slate-400 ml-auto">from {dx.parentFlagCategory}</span>}
                    </div>
                    <div className="text-[12px] text-slate-700"><span className="font-semibold">Test to rule out:</span> {dx.testToRuleOut}</div>
                  </div>
                ))}
              </div>
            </AccSection>
          )}

          {riskFlags.length > 0 && (
            <AccSection value="riskdetail" title="Risk flags — full detail &amp; provenance" testid="risk-detail" badge={riskFlags.length}>
              <div className="space-y-2">
                {riskFlags.map((flag, i) => <RiskFlagCard key={i} flag={flag} />)}
              </div>
            </AccSection>
          )}

          <AccSection value="evidence" title="Evidence map — validated instruments" testid="evidence-map">
            <EvidenceMapBadges riskFlags={riskFlags} evidenceMap={result.evidenceMap} />
          </AccSection>

          {hasTakeHome && (
            <AccSection value="takehome" title="Patient take-home" testid="patient-take-home">
              <div className="flex flex-wrap gap-2 mb-3">
                <Button size="sm" variant="outline" onClick={() => downloadPDF(`${API}/encounter/${encounterId}/patient-pdf`)} data-testid="btn-take-home-pdf">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download patient take-home (PDF)
                </Button>
              </div>
              {(soap.patientBridge || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-slate-600 mb-1">Questions to ask your doctor</div>
                  <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-700">
                    {soap.patientBridge.map((q, i) => <li key={i}>{q}</li>)}
                  </ol>
                </div>
              )}
              {motivationSupport && (
                <div className={`rounded-md p-3 mb-3 ${motivationSupport.blocked ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <h5 className={`font-bold text-sm mb-1 ${motivationSupport.blocked ? 'text-red-800' : 'text-emerald-800'}`}>{motivationSupport.heading}</h5>
                  <p className={`text-sm ${motivationSupport.blocked ? 'text-red-700' : 'text-slate-700'}`}>{motivationSupport.message}</p>
                  {motivationSupport.nextStep && <p className="text-sm text-slate-800 mt-2"><span className="font-semibold">Next step:</span> {motivationSupport.nextStep}</p>}
                </div>
              )}
              {providerFollowup.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">Next-visit follow-up prompts</div>
                  <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-700">
                    {providerFollowup.map((q, i) => <li key={i}>{q}</li>)}
                  </ol>
                </div>
              )}
            </AccSection>
          )}

          {clinicalSummary && (
            <AccSection value="summary" title="Clinical summary" testid="clinical-summary">
              <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans bg-slate-50 p-4 rounded-md">{clinicalSummary}</pre>
            </AccSection>
          )}

          {auditLog.length > 0 && (
            <AccSection value="timeline" title="Decision timeline (audit log)" testid="decision-timeline" badge={auditLog.length}>
              <div className="relative pl-6 border-l-2 border-slate-200 space-y-3">
                {auditLog.map((entry, i) => (
                  <div key={i} className="relative" data-testid={`audit-entry-${i}`}>
                    <div className="absolute -left-[25px] w-3 h-3 rounded-full border-2 border-white" style={{ background: surf.border }} />
                    <div className="p-2.5 rounded-md bg-slate-50 text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-slate-800">{entry.action}</span>
                        <span className="text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">{entry.status}</span>
                      </div>
                      <p className="text-slate-500">{entry.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </AccSection>
          )}

          {guardrails && (
            <AccSection value="guardrails" title="Guardrails" testid="guardrails">
              <div className={`p-3 rounded-md mb-3 ${guardrails.passed ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center gap-2">
                  {guardrails.passed ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
                  <span className="font-semibold text-sm">{guardrails.status}</span>
                </div>
              </div>
              <div className="space-y-1.5">
                {(guardrails.checks || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded bg-slate-50 text-sm text-slate-700">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" /> {c}
                  </div>
                ))}
                {(guardrails.issues || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded bg-red-50 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 text-red-500" /> {c}
                  </div>
                ))}
              </div>
            </AccSection>
          )}

          {healthMartRecommendations.length > 0 && (
            <AccSection value="healthmart" title="Health Mart" testid="healthmart" badge={healthMartRecommendations.length}>
              <div className="space-y-2">
                {healthMartRecommendations.map((rec, i) => (
                  <div key={i} className="p-3 rounded-md bg-white border border-slate-200">
                    <div className="text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 inline-block mb-1">{rec.category}</div>
                    <h5 className="font-semibold text-sm text-slate-900">{rec.title}</h5>
                    <p className="text-xs text-slate-600 mt-0.5">{rec.description}</p>
                  </div>
                ))}
              </div>
            </AccSection>
          )}

          {hasPsychosocial && (
            <AccSection value="psychosocial" title="Psychosocial &amp; support resources" testid="psychosocial">
              {lonelinessSupport?.card && (
                <div className="mb-3 rounded-md p-3 bg-indigo-50 border border-indigo-200" data-testid="loneliness-support-card">
                  <h5 className="font-bold text-sm text-indigo-900 mb-1" data-testid="loneliness-support-title">{lonelinessSupport.card.title}</h5>
                  <p className="text-sm text-slate-800" data-testid="loneliness-support-body">{lonelinessSupport.card.body}</p>
                </div>
              )}
              <PsychosocialSession encounterId={encounterId} offer={psychosocialOffer} />
              <PatientCaregiverResources resourceBundles={resourceBundles} />
            </AccSection>
          )}

          <AccSection value="structured" title="Structured data (JSON)" testid="structured-data-note">
            <p data-testid="structured-data-note" className="text-xs text-slate-500 italic">Raw structured output has moved to the <strong>Investor</strong> tab (determinism proof) — it carries no clinical meaning for this visit.</p>
          </AccSection>
        </Accordion>
        </>
        )}

        {/* Attestation modal */}
        {showAttestation && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="attestation-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-3">Active Assertive Attestation</h3>
              <p className="text-sm text-slate-600 mb-4">Before exporting this SOAP note, please confirm both statements. This attestation is logged with the encounter record (Cures Act / SaMD alignment).</p>
              <label className="flex items-start gap-3 mb-3 text-sm text-slate-800 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={attestChecked1} onChange={(e) => setAttestChecked1(e.target.checked)} data-testid="attest-check-1" />
                <span>I explicitly certify that I have read the generated clinical note in its entirety and have verified that all differentials and risk flags match my independent, final medical judgment.</span>
              </label>
              <label className="flex items-start gap-3 mb-5 text-sm text-slate-800 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={attestChecked2} onChange={(e) => setAttestChecked2(e.target.checked)} data-testid="attest-check-2" />
                <span>I understand P.AI Global Health acts strictly as an administrative, rule-based decision support tool, and I am the sole diagnostic decision-maker for clinical action regarding this patient.</span>
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setShowAttestation(false); setAttestChecked1(false); setAttestChecked2(false); }} data-testid="attest-cancel">Cancel</Button>
                <Button
                  size="sm"
                  disabled={!attestChecked1 || !attestChecked2}
                  className="text-white"
                  style={{ background: surf.border }}
                  onClick={() => {
                    setShowAttestation(false); setAttestChecked1(false); setAttestChecked2(false);
                    downloadPDF(`${API}/encounter/${encounterId}/soap-pdf`);
                  }}
                  data-testid="attest-confirm"
                >Confirm &amp; Download SOAP PDF</Button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </section>
  );
}
